import { css } from "styled-components";

import { cssColorsFromBackgroundField } from "./background";
import {
    BackgroundField,
    Properties,
    Size,
    Position,
    Image,
    Angle
} from "./background-field";
import { breakpoints } from "./breakpoints";
import { colorCSS } from "./colors";
import { gradientExists, gradients } from "./gradients";
import { redundantCssFromKebab } from "./redundant-css";

export function generateFieldsStyles(
    fields: BackgroundField[]
): ReturnType<typeof css> {
    const allStyles = fields.map((field) => generateFieldStyles(field));
    return css`
        ${allStyles}
    `;
}

export function generateFieldStyles(
    field: BackgroundField
): ReturnType<typeof css> {
    const allBreakpointStyles: ReturnType<typeof css>[] = [];

    field.breakpoints.forEach((breakpoint) => {
        if (breakpoint === "xs") {
            allBreakpointStyles.push(
                generateBreakpoint(
                    field.color,
                    field.properties,
                    breakpoints.xs,
                    breakpoints.sm
                )
            );
        }
        if (breakpoint === "sm") {
            allBreakpointStyles.push(
                generateBreakpoint(
                    field.color,
                    field.properties,
                    breakpoints.sm,
                    breakpoints.md
                )
            );
        }
        if (breakpoint === "md") {
            allBreakpointStyles.push(
                generateBreakpoint(
                    field.color,
                    field.properties,
                    breakpoints.md,
                    breakpoints.lg
                )
            );
        }
        if (breakpoint === "lg") {
            allBreakpointStyles.push(
                generateBreakpoint(
                    field.color,
                    field.properties,
                    breakpoints.lg,
                    breakpoints.xl
                )
            );
        }
        if (breakpoint === "xl") {
            allBreakpointStyles.push(
                generateBreakpoint(
                    field.color,
                    field.properties,
                    breakpoints.xl
                )
            );
        }
    });

    return css`
        ${allBreakpointStyles}
    `;
}

export function generateBreakpoint(
    color: string,
    propertiesArr: Properties[],
    lower: number,
    upper?: number
): ReturnType<typeof css> {
    const mediaQuery = createMediaQuery(lower, upper);
    const bgStyles = createBackground(color, propertiesArr);

    // Return empty if no styles to apply
    if (!bgStyles || (Array.isArray(bgStyles) && bgStyles.length === 0)) {
        return css``;
    }

    return css`
        @media ${mediaQuery} {
            ${bgStyles}
        }
    `;
}

export function createMediaQuery(lower: number, upper?: number) {
    if (upper) {
        return `(min-width: ${lower}px) and (max-width: ${upper - 1}px)`;
    } else {
        return `(min-width: ${lower}px)`;
    }
}

export function createBackground(
    color: string,
    propertiesArr: Properties[]
): ReturnType<typeof css> {
    const textColor = createTextColor(color);

    if (!propertiesArr.length) {
        return textColor || css``;
    }

    const bgProps = createBackgroundProperties(propertiesArr);
    return textColor
        ? css`
              ${bgProps}
              ${textColor}
          `
        : bgProps;
}

export function createBackgroundProperties(
    propertiesArr: Properties[]
): ReturnType<typeof css> {
    return css`
        ${redundantCssFromKebab(
            "background-attachment",
            `${generateAttachment(propertiesArr)}`
        )}
        ${redundantCssFromKebab(
            "background-blend-mode",
            `${generateBlendMode(propertiesArr)}`
        )}
        ${redundantCssFromKebab(
            "background-clip",
            `${generateClip(propertiesArr)}`
        )}
        ${redundantCssFromKebab(
            "background-image",
            `${generateImage(propertiesArr)}`
        )}
        ${redundantCssFromKebab(
            "background-origin",
            `${generateOrigin(propertiesArr)}`
        )}
        ${redundantCssFromKebab(
            "background-position",
            `${generatePosition(propertiesArr)}`
        )}
        ${redundantCssFromKebab(
            "background-repeat",
            `${generateRepeat(propertiesArr)}`
        )}
        ${redundantCssFromKebab(
            "background-size",
            `${generateSize(propertiesArr)}`
        )}
    `;
}

export function createTextColor(color: string): ReturnType<typeof css> | null {
    return color === "transparent"
        ? css`
              ${redundantCssFromKebab("background-color", "transparent")}
          `
        : cssColorsFromBackgroundField({ color });
}

export function generateAttachment(propertiesArr: Properties[]) {
    return propertiesArr.map((properties) => properties.attachment).join(", ");
}

export function generateBlendMode(propertiesArr: Properties[]) {
    return propertiesArr.map((properties) => properties.blendMode).join(", ");
}

export function generateClip(propertiesArr: Properties[]) {
    return propertiesArr.map((properties) => properties.clip).join(", ");
}

export function generateImage(propertiesArr: Properties[]) {
    return propertiesArr
        .map((properties) => convertImageToValue(properties.image))
        .join(", ");
}

export function generateOrigin(propertiesArr: Properties[]) {
    return propertiesArr.map((properties) => properties.origin).join(", ");
}

export function generateRepeat(propertiesArr: Properties[]) {
    return propertiesArr.map((properties) => properties.repeat).join(", ");
}

export function generateSize(propertiesArr: Properties[]) {
    return propertiesArr
        .map((properties) => convertSizeToValue(properties.size))
        .join(", ");
}

export function generatePosition(propertiesArr: Properties[]) {
    return propertiesArr
        .map((properties) => convertPositionToValue(properties.position))
        .join(", ");
}

export function convertImageToValue(image: Image) {
    if (image.type === "image") {
        if (image.image.value === "none") {
            return "none";
        } else {
            return `url("${image.image.value}")`;
        }
    } else {
        return convertGradientToValue(
            image.gradient.value,
            image.gradient.angle
        );
    }
}

export function convertPositionToValue(position: Position) {
    if (position.fieldType === "keywords") {
        //hack for safari subpixel rounding bug
        const positionVertical =
            position.keywords.vertical === "bottom"
                ? "bottom -0.5px"
                : position.keywords.vertical;
        return `${position.keywords.horizontal} ${positionVertical}`;
    } else {
        return `${position.units.horizontal}${position.units.unit} ${position.units.vertical}${position.units.unit}`;
    }
}

export function convertSizeToValue(size: Size) {
    if (size.fieldType === "keyword") {
        return size.keyword;
    } else {
        const width = size.units.isAutoWidth
            ? "auto"
            : `${size.units.width}${size.units.unit}`;
        const height = size.units.isAutoHeight
            ? "auto"
            : `${size.units.height}${size.units.unit}`;
        return `${width} ${height}`;
    }
}

export function convertGradientToValue(
    gradientId: string,
    angle: Angle
): string {
    if (gradientExists(gradientId)) {
        const cssForStops: string = gradients[gradientId].colors
            .map((colorId) => colorCSS(colorId))
            .join(",");
        return `linear-gradient(${angle}deg, ${cssForStops})`;
    } else {
        return "none";
    }
}
